import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  NumberInput,
  SegmentedControl,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconAlertCircle, IconDownload, IconRefresh } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import HttpClient from '../helpers/HttpClient';
import { config } from '../helpers/Config';
import { getAccessToken } from '../helpers/TokenRefresh';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;
const MONTH_LABELS = ['Jän', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
type MonthKey = typeof MONTHS[number];

interface PraeRow {
  personId: number;
  firstName: string;
  lastName: string;
  svNumber?: string;
  firstActivityDate?: string;
  lastActivityDate?: string;
  months: Record<MonthKey, number>;
  total: number;
  isManuallyOverridden: boolean;
  recordId?: number;
}

const client = new HttpClient<PraeRow>('/prae');

export default function Prae() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [mode, setMode] = useState<'einreichung' | 'storno'>('einreichung');
  const [rows, setRows] = useState<PraeRow[]>([]);
  const [edited, setEdited] = useState<Map<number, Record<MonthKey, number>>>(new Map());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    setLoading(true);
    setEdited(new Map());
    try {
      const data = await client.getAll({ year: String(year) });
      setRows(data ?? []);
    } catch {
      notifications.show({ message: 'Laden fehlgeschlagen', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year]);

  const getMonths = (row: PraeRow): Record<MonthKey, number> => {
    return edited.get(row.personId) ?? row.months;
  };

  const setMonth = (personId: number, month: MonthKey, value: number, originalMonths: Record<MonthKey, number>) => {
    setEdited(prev => {
      const next = new Map(prev);
      const current = next.get(personId) ?? { ...originalMonths };
      next.set(personId, { ...current, [month]: value });
      return next;
    });
  };

  const getTotal = (row: PraeRow) => {
    const m = getMonths(row);
    return MONTHS.reduce((sum, k) => sum + (m[k] ?? 0), 0);
  };

  const saveOverride = async (row: PraeRow) => {
    const months = edited.get(row.personId);
    if (!months) return;
    try {
      await client.postAction('override', { personId: row.personId, year, months });
      notifications.show({ message: 'Gespeichert', color: 'green' });
      await load();
    } catch {
      notifications.show({ message: 'Speichern fehlgeschlagen', color: 'red' });
    }
  };

  const resetOverride = async (row: PraeRow) => {
    try {
      const token = await getAccessToken();
      await fetch(`${config.apiUrl}/prae/override/${row.personId}/${year}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ message: 'Zurückgesetzt', color: 'green' });
      await load();
    } catch {
      notifications.show({ message: 'Zurücksetzen fehlgeschlagen', color: 'red' });
    }
  };

  const exportZip = async () => {
    setExporting(true);
    try {
      const token = await getAccessToken();
      const res = await fetch(
        `${config.apiUrl}/prae/export?year=${year}&mode=${mode}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `PRAE_${year}_${mode}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notifications.show({ message: 'Export fehlgeschlagen', color: 'red' });
    } finally {
      setExporting(false);
    }
  };

  const yearOptions = Array.from({ length: 5 }, (_, i) => {
    const y = new Date().getFullYear() - 2 + i;
    return { value: String(y), label: String(y) };
  });

  return (
    <Box>
      <Title order={3} mb="md">PRAE – L19 ELDA Export</Title>

      <Group mb="md" align="flex-end" wrap="wrap">
        <SegmentedControl
          data={yearOptions}
          value={String(year)}
          onChange={(v) => setYear(Number(v))}
        />
        <SegmentedControl
          data={[
            { value: 'einreichung', label: 'Einreichung' },
            { value: 'storno', label: 'Storno' },
          ]}
          value={mode}
          onChange={(v) => setMode(v as 'einreichung' | 'storno')}
        />
        <Button leftSection={<IconDownload size="1rem" />} onClick={exportZip} loading={exporting} disabled={rows.length === 0}>
          ZIP exportieren
        </Button>
      </Group>

      {rows.length === 0 && !loading && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="blue">
          Keine PRAE-relevanten Buchungen für {year} gefunden. Buchungsarten mit "PRAE-relevant" markieren und Buchungen erfassen.
        </Alert>
      )}

      {loading ? (
        <Loader mt="xl" />
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table withColumnBorders withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ minWidth: 160 }}>Person</Table.Th>
                <Table.Th style={{ minWidth: 110 }}>SVN / Typ</Table.Th>
                {MONTH_LABELS.map(m => (
                  <Table.Th key={m} style={{ minWidth: 75, textAlign: 'right' }}>{m}</Table.Th>
                ))}
                <Table.Th style={{ minWidth: 90, textAlign: 'right' }}>Gesamt</Table.Th>
                <Table.Th style={{ minWidth: 90 }}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map(row => {
                const months = getMonths(row);
                const total = getTotal(row);
                const isDirty = edited.has(row.personId);
                const overLimit = total > 8640;
                const anyMonthOver = MONTHS.some(m => (months[m] ?? 0) > 720);

                return (
                  <Table.Tr key={row.personId}>
                    <Table.Td>
                      <Group gap={4}>
                        <Text size="sm">{row.lastName} {row.firstName}</Text>
                        {row.isManuallyOverridden && (
                          <Tooltip label="Manuell angepasst">
                            <Badge size="xs" color="orange">M</Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {row.svNumber ? (
                        <Text size="xs" c="dimmed">{row.svNumber}</Text>
                      ) : (
                        <Badge size="xs" color="grape">NonAT</Badge>
                      )}
                    </Table.Td>
                    {MONTHS.map((m) => {
                      const val = months[m] ?? 0;
                      const over = val > 720;
                      return (
                        <Table.Td key={m} style={{ padding: '4px 6px' }}>
                          <NumberInput
                            size="xs"
                            value={val}
                            min={0}
                            max={9999}
                            decimalScale={2}
                            decimalSeparator=","
                            hideControls
                            styles={{
                              input: {
                                textAlign: 'right',
                                width: 68,
                                backgroundColor: over ? 'var(--mantine-color-red-1)' : undefined,
                                color: over ? 'var(--mantine-color-red-8)' : undefined,
                              },
                            }}
                            onChange={(v) => setMonth(row.personId, m, Number(v) || 0, row.months)}
                          />
                        </Table.Td>
                      );
                    })}
                    <Table.Td style={{ textAlign: 'right' }}>
                      <Text
                        size="sm"
                        fw={600}
                        c={overLimit ? 'red' : anyMonthOver ? 'orange' : undefined}
                      >
                        {total.toFixed(2).replace('.', ',')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} wrap="nowrap">
                        {isDirty && (
                          <Button size="xs" onClick={() => saveOverride(row)}>
                            Speichern
                          </Button>
                        )}
                        {row.isManuallyOverridden && !isDirty && (
                          <Tooltip label="Auf Buchungswerte zurücksetzen">
                            <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => resetOverride(row)}>
                              <IconRefresh size="0.9rem" />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
